import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/custom/verify-email`, {
        token
      });
      
      setEmail(response.data.email || '');
      setStatus('success');
      toast.success('Email verified successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { verified: true } });
      }, 3000);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      setErrorMessage(message);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-wantd-navy.png"
            alt="WANTD" 
            className="h-12 w-auto"
          />
        </Link>

        {/* Card */}
        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
          
          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#C9956C] animate-spin" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Verifying Your Email
              </h1>
              <p className="text-[var(--theme-text-secondary)]">
                Please wait a moment...
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Email Verified!
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                Your email has been verified successfully.
                {email && (
                  <>
                    <br />
                    <strong className="text-[var(--theme-text-primary)]">{email}</strong>
                  </>
                )}
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mb-6">
                Redirecting to login...
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                data-testid="continue-to-login-btn"
              >
                Continue to Sign In
              </Button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Verification Failed
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                {errorMessage}
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                data-testid="back-to-login-btn"
              >
                Back to Sign In
              </Button>
              <p className="text-sm text-[var(--theme-text-muted)] mt-4">
                Need a new verification link?{' '}
                <Link to="/login" className="text-[#C9956C] hover:text-[#B8845B] font-medium">
                  Sign in to resend
                </Link>
              </p>
            </div>
          )}

          {/* No Token State */}
          {status === 'no-token' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Invalid Link
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                This verification link is invalid or has expired.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
